import { AnimatePresence, motion } from "framer-motion";
import { RouterProvider, useRouter } from "./router";
import Layout from "./components/Layout";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import About from "./pages/About";
import Projects from "./pages/Projects";
import Blog from "./pages/Blog";
import NotFound from "./pages/NotFound";
import Offline from "./pages/Offline";

import ProjectDetail from "./pages/ProjectDetail";
import BlogDetail from "./pages/BlogDetail";
import { useEffect, useState } from "react";

const ROUTES = ['/', '/about', '/projects', '/blog'];

function Content() {
  const { path, navigate } = useRouter();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [direction, setDirection] = useState(0); // 1 = down (next), -1 = up (prev)

  // Keyboard Navigation Logic
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if no modal/input is active (simplified check)
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;

      const currentIndex = ROUTES.indexOf(path);
      if (currentIndex === -1) return; // Not a main route

      if (e.key === 'ArrowDown') {
        // Check if at bottom
        const scrolledToBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 10; // Tolerance
        if (scrolledToBottom && currentIndex < ROUTES.length - 1) {
          e.preventDefault();
          setDirection(1);
          navigate(ROUTES[currentIndex + 1]);
        }
      } else if (e.key === 'ArrowUp') {
        // Check if at top
        const scrolledToTop = window.scrollY === 0;
        if (scrolledToTop && currentIndex > 0) {
          e.preventDefault();
          setDirection(-1);
          navigate(ROUTES[currentIndex - 1]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [path, navigate]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const renderPage = () => {
    if (path === '/') return <Home key="home" />;
    if (path === '/about') return <About key="about" />;
    if (path === '/projects') return <Projects key="projects" />;
    if (path === '/blog') return <Blog key="blog" />;

    // Dynamic Routes
    if (path.startsWith('/projects/')) {
      const slug = path.split('/projects/')[1] || '';
      if (!slug) return <NotFound key="404" />;
      return <ProjectDetail key={`project-${slug}`} slug={slug} />;
    }
    if (path.startsWith('/blog/')) {
      const slug = path.split('/blog/')[1] || '';
      if (!slug) return <NotFound key="404" />;
      return <BlogDetail key={`blog-${slug}`} slug={slug} />;
    }

    return <NotFound key="404" />;
  };

  if (!isOnline) {
    return <Offline />;
  }

  // Animation Variants
  const variants = {
    enter: (direction: number) => ({
      y: direction > 0 ? "5%" : "-5%",
      opacity: 0,
      scale: 0.99,
    }),
    center: {
      y: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (direction: number) => ({
      y: direction > 0 ? "-2%" : "2%",
      opacity: 0,
      scale: 0.99,
      transition: { duration: 0.01, ease: "linear" }
    }),
  };

  return (
    <Layout>
      <Navbar />
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={path}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            y: { type: "tween", ease: "circOut", duration: 0.2 },
            opacity: { duration: 0.15 },
            scale: { duration: 0.15 }
          }}
          className="w-full"
        >
          {renderPage()}
        </motion.div>
      </AnimatePresence>
    </Layout>
  );
}

import { DataProvider } from "./context/DataContext";

function App() {
  return (
    <RouterProvider>
      <DataProvider>
        <Content />
      </DataProvider>
    </RouterProvider>
  );
}

export default App;
